// Un menú que en escritorio es un popover anclado al botón y en el teléfono una hoja que sube
// desde abajo (ADR-2026-10-22-los-menus-de-categoria-suben-desde-abajo). La hoja usa el mismo
// chrome y la misma animación que `SheetLateral` en mobile (manija, "Cancelar · título",
// 300 ms, `useExitTransition` para que la salida también se vea), así los menús de categoría y
// el panel de editarlas se mueven igual.
import React, { useEffect, useState, type RefObject } from "react";
import ReactDOM from "react-dom";
import PopoverAnclado from "../pages/lanzamientos/PopoverAnclado";
import { useExitTransition } from "../hooks/useExitTransition";

/** El mismo corte que `SheetLateral` (`md`): debajo, hoja desde abajo. */
const CONSULTA_TELEFONO = "(max-width: 767px)";

export function useEsTelefono(): boolean {
  const [es, setEs] = useState(
    () => typeof window !== "undefined" && window.matchMedia(CONSULTA_TELEFONO).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(CONSULTA_TELEFONO);
    const alCambiar = () => setEs(mq.matches);
    mq.addEventListener("change", alCambiar);
    return () => mq.removeEventListener("change", alCambiar);
  }, []);
  return es;
}

function HojaDesdeAbajo({
  abierto,
  onCerrar,
  titulo,
  capa,
  children,
}: {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  capa: number;
  children: React.ReactNode;
}) {
  const { mounted, animated } = useExitTransition(abierto, 300);
  useEffect(() => {
    if (!abierto) return;
    const alaTecla = (e: KeyboardEvent) => e.key === "Escape" && onCerrar();
    document.addEventListener("keydown", alaTecla);
    return () => document.removeEventListener("keydown", alaTecla);
  }, [abierto, onCerrar]);
  if (!mounted) return null;
  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 ${animated ? "opacity-100" : "opacity-0"}`}
        style={{ zIndex: capa - 1 }}
        onClick={onCerrar}
      />
      <div
        role="dialog"
        aria-label={titulo}
        className={`fixed inset-x-0 bottom-0 flex max-h-[85dvh] flex-col rounded-t-[13px] bg-white shadow-2xl transition-transform duration-300 ease-out ${
          animated ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ zIndex: capa, paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="shrink-0 py-2">
          <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.15]" />
        </div>
        <div className="flex shrink-0 items-center justify-between px-4 pb-2">
          <button
            type="button"
            onClick={onCerrar}
            className="w-20 text-left text-[15px] text-brand-primary transition-opacity active:opacity-50"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">{titulo}</h3>
          <div className="w-20" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </div>
    </>,
    document.body,
  );
}

export function MenuDesplegable({
  abierto,
  onCerrar,
  anclaRef,
  titulo,
  ancho,
  alineacion,
  capa = 200,
  children,
}: {
  abierto: boolean;
  onCerrar: () => void;
  anclaRef: RefObject<HTMLElement | null>;
  /** El título de la hoja en el teléfono. */
  titulo: string;
  ancho?: number;
  alineacion?: "izquierda" | "derecha";
  capa?: number;
  /** Recibe si va en la hoja del teléfono (para no abrir el teclado solo, por ejemplo). */
  children: (enTelefono: boolean) => React.ReactNode;
}) {
  const enTelefono = useEsTelefono();
  if (enTelefono) {
    return (
      <HojaDesdeAbajo abierto={abierto} onCerrar={onCerrar} titulo={titulo} capa={capa}>
        {children(true)}
      </HojaDesdeAbajo>
    );
  }
  return (
    <PopoverAnclado
      abierto={abierto}
      anclaRef={anclaRef}
      onCerrar={onCerrar}
      ancho={ancho}
      alineacion={alineacion}
      capa={capa}
      scrollPropio
    >
      {children(false)}
    </PopoverAnclado>
  );
}
